class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (!searchParams) {
      if (window.location.pathname.includes("/search")) {
        console.log(
          "renderPage called with empty searchParams on a /search page, aborting."
        );
        return;
      }
    }
    FacetFiltersForm.searchParamsPrev = searchParams;

    // Retrieve current min_price and max_price from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const minPrice = urlParams.get("min_price");
    const maxPrice = urlParams.get("max_price");

    // Include min_price and max_price in searchParams if they exist
    if (minPrice) searchParams += `&filter.v.price.gte=${minPrice}`;
    if (maxPrice) searchParams += `&filter.v.price.lte=${maxPrice}`;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter")
      );
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    // Ensure that the active facets are not disabled
    FacetFiltersForm.toggleActiveFacets(false);

    // Re-apply the active filters
    const activeFilters = document.querySelectorAll(".js-facet-remove");
    activeFilters.forEach((filter) => {
      filter.classList.remove("disabled");
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form")
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            const noJsElements = document.querySelectorAll(".no-js-list");
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    // Get all elements
    this.minInput = this.querySelector("input#rangeMin");
    this.maxInput = this.querySelector("input#rangeMax");
    this.minValueDisplay = this.querySelector("#rangeMinValue");
    this.maxValueDisplay = this.querySelector("#rangeMaxValue");
    this.sliderRange = this.querySelector(".slider-range");

    this.minInput.addEventListener("change", this.updateFilters.bind(this));
    this.maxInput.addEventListener("change", this.updateFilters.bind(this));

    this.minInput.addEventListener("input", this.handleMinInput.bind(this));
    this.maxInput.addEventListener("input", this.handleMaxInput.bind(this));

    this.updateDisplayValues();
    this.updateSliderRange();
  }

  // Handle not letting the min/max sliders pass each other
  handleMinInput() {
    if (parseFloat(this.minInput.value) > parseFloat(this.maxInput.value)) {
      this.minInput.value = this.maxInput.value;
    }
    this.updateDisplayValues();
    this.updateSliderRange();
  }
  handleMaxInput() {
    if (parseFloat(this.maxInput.value) < parseFloat(this.minInput.value)) {
      this.maxInput.value = this.minInput.value;
    }
    this.updateDisplayValues();
    this.updateSliderRange();
  }

  // Update filters
  updateFilters() {
    const minInput = this.minInput.value;
    const maxInput = this.maxInput.value;
    const url = new URL(window.location);
    url.searchParams.set("min_price", minInput);
    url.searchParams.set("max_price", maxInput);
    window.history.pushState({}, "", url);
    this.refreshFilters();
  }

  // Update the display number values of what the slider value is
  updateDisplayValues() {
    this.minValueDisplay.textContent = this.minInput.value;
    this.maxValueDisplay.textContent = this.maxInput.value;
  }

  updateSliderRange() {
    const min = parseFloat(this.minInput.value);
    const max = parseFloat(this.maxInput.value);
    const minRange = parseFloat(this.minInput.min);
    const maxRange = parseFloat(this.maxInput.max);

    const rangeWidth = ((max - min) / (maxRange - minRange)) * 100;
    const rangeLeft = ((min - minRange) / (maxRange - minRange)) * 100;

    this.sliderRange.style.width = `${rangeWidth}%`;
    this.sliderRange.style.left = `${rangeLeft}%`;
  }

  refreshFilters() {
    // Store the current state of the filters
    const currentState = {
      min_price: this.minInput.value,
      max_price: this.maxInput.value,
    };

    // Re-apply the stored state after the refresh
    this.applyStoredState(currentState);
    this.updateDisplayValues();
    this.updateSliderRange();
  }

  applyStoredState(state) {
    this.minInput.value = state.min_price;
    this.maxInput.value = state.max_price;

    const url = new URL(window.location);
    url.searchParams.set("min_price", state.min_price);
    url.searchParams.set("max_price", state.max_price);
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    if (this.classList.contains("facet-remove-all")) {
      // Clear all active filters
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      const url = window.location.pathname; // Reset to base URL without filters
      FacetFiltersForm.renderPage(url);
    } else {
      // Remove a single filter
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define("facet-remove", FacetRemove);
